import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { 
  Upload, 
  FileText, 
  TrendingUp, 
  AlertCircle, 
  CheckCircle2, 
  XCircle,
  Target,
  Zap,
  Download,
  RefreshCw,
  Eye,
  Sparkles,
  X
} from "lucide-react";
import { ATSScoreGauge } from "@/components/ats/ATSScoreGauge";
import { ATSCategoryScores } from "@/components/ats/ATSCategoryScores";
import { ATSIssuesList } from "@/components/ats/ATSIssuesList";
import { ATSKeywordAnalysis } from "@/components/ats/ATSKeywordAnalysis";
import { ATSSystemCompatibility } from "@/components/ats/ATSSystemCompatibility";
import { ATSRecommendations } from "@/components/ats/ATSRecommendations";
import { ATSParsingPreview } from "@/components/ats/ATSParsingPreview";
import { ATSCompetitorComparison } from "@/components/ats/ATSCompetitorComparison";
import { SubscriptionGate } from "@/components/SubscriptionGate";
import { useSubscription } from "@/hooks/useSubscription";
import { useJobContext } from "@/contexts/JobContext";

interface ATSAnalysis {
  overall_score: number;
  formatting_score: number;
  keywords_score: number;
  content_score: number;
  technical_score: number;
  strengths: string[];
  critical_issues: Array<{ issue: string; location: string; fix: string }>;
  warnings: Array<{ issue: string; suggestion: string }>;
  opportunities: string[];
  keyword_analysis?: {
    exact_matches: string[];
    missing_keywords: string[];
    keyword_density: number;
    context_score: number;
  };
  ats_compatibility: Array<{
    system: string;
    score: number;
    issues: string[];
  }>;
  industry_insights?: {
    industry: string;
    recommendations: string[];
  };
  estimated_improvement: number;
  parsing_preview: {
    raw_text: string;
    sections: Array<{
      section: string;
      extracted: boolean;
      content: string;
      issues?: string[];
    }>;
  };
}

const AtsScoreChecker = () => {
  const navigate = useNavigate();
  const { hasFeature } = useSubscription();
  const { selectedJob, clearSelectedJob } = useJobContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploadMethod, setUploadMethod] = useState<"select" | "upload">("select");
  const [resumes, setResumes] = useState<any[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [selectedResumeId, setSelectedResumeId] = useState<string>("");
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [jobDescription, setJobDescription] = useState("");
  const [selectedIndustry, setSelectedIndustry] = useState("tech");
  const [selectedJobLevel, setSelectedJobLevel] = useState("mid");
  const [analysis, setAnalysis] = useState<ATSAnalysis | null>(null);
  const [previousScore, setPreviousScore] = useState<number | null>(null);

  // Auto-populate from JobContext
  useEffect(() => {
    if (selectedJob) {
      const fullJobText = `${selectedJob.title} at ${selectedJob.company}\n${selectedJob.location || ''}\n\n${selectedJob.description || ''}`;
      setJobDescription(fullJobText);
      toast.success(`Checking ATS for: ${selectedJob.title} at ${selectedJob.company}`);
    }
  }, [selectedJob]);

  useEffect(() => {
    fetchResumesAndDocuments();
    loadScannedJob();
    
    // Listen for job data updates from floating panel
    const handleJobDataUpdate = (e: CustomEvent) => {
      const jobData = e.detail;
      const fullJobText = `${jobData.title} at ${jobData.company}\n${jobData.location}\n\n${jobData.description}`;
      setJobDescription(fullJobText);
      toast.success(`Job loaded: ${jobData.title} at ${jobData.company}`);
    };
    
    window.addEventListener('jobDataUpdated', handleJobDataUpdate as EventListener);
    
    return () => {
      window.removeEventListener('jobDataUpdated', handleJobDataUpdate as EventListener);
    };
  }, []);

  const loadScannedJob = () => {
    const scannedJobStr = sessionStorage.getItem('scannedJob');
    if (scannedJobStr) {
      try {
        const scannedJob = JSON.parse(scannedJobStr);
        const fullJobText = `${scannedJob.title} at ${scannedJob.company}\n${scannedJob.location}\n\n${scannedJob.description}`;
        setJobDescription(fullJobText);
        sessionStorage.removeItem('scannedJob');
        toast.success(`Job loaded: ${scannedJob.title} at ${scannedJob.company}`);
      } catch (error) {
        console.error('Failed to load scanned job:', error);
      }
    }
  };

  const fetchResumesAndDocuments = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/login");
        return;
      }

      // Fetch resume versions
      const { data: resumeData, error: resumeError } = await supabase
        .from("resume_versions")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (resumeError) throw resumeError;
      setResumes(resumeData || []);

      // Fetch documents (resumes)
      const { data: docData, error: docError } = await supabase
        .from("documents")
        .select("*")
        .eq("user_id", user.id)
        .eq("kind", "resume")
        .order("created_at", { ascending: false });

      if (docError) throw docError;
      setDocuments(docData || []);
    } catch (error) {
      console.error("Error fetching resumes:", error);
      toast.error("Failed to load resumes");
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = [
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/msword",
        "text/plain"
      ];
      
      if (!validTypes.includes(file.type)) {
        toast.error("Please upload a PDF, DOCX, DOC, or TXT file");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB");
        return;
      }

      setUploadedFile(file);
      toast.success(`File "${file.name}" selected`);
    }
  };

  const analyzeResume = async () => {
    // Validation
    if (uploadMethod === "select" && !selectedResumeId && !selectedDocumentId) {
      toast.error("Please select a resume to analyze");
      return;
    }

    if (uploadMethod === "upload" && !uploadedFile) {
      toast.error("Please upload a resume file");
      return;
    }

    setAnalyzing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      let resumeId = selectedResumeId;

      // Handle file upload
      if (uploadMethod === "upload" && uploadedFile) {
        // Upload file to storage
        const fileExt = uploadedFile.name.split('.').pop();
        const fileName = `${user.id}/${Date.now()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(fileName, uploadedFile);

        if (uploadError) throw uploadError;

        // Create document record
        const { data: docData, error: docError } = await supabase
          .from('documents')
          .insert({
            user_id: user.id,
            title: uploadedFile.name,
            storage_path: fileName,
            format: fileExt as any,
            kind: 'resume',
            bytes: uploadedFile.size
          })
          .select()
          .single();

        if (docError) throw docError;
        resumeId = docData.id;
      } else if (uploadMethod === "select" && selectedDocumentId) {
        resumeId = selectedDocumentId;
      }

      // Store previous score for before/after comparison
      const selectedResume = resumes.find(r => r.id === resumeId);
      if (selectedResume?.ats_score) {
        setPreviousScore(selectedResume.ats_score);
      }

      // Generate job hash for idempotency
      const encoder = new TextEncoder();
      const contextData = `${resumeId}:${jobDescription.substring(0, 200)}`;
      const hashBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(contextData));
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const jobHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 16);
      const refId = `ats:${resumeId}:${jobHash}:${Date.now()}`;

      // Phase 1: Charge credits atomically
      const { data: creditResult, error: creditError } = await supabase.functions.invoke('credits-spend', {
        body: { featureKey: 'ats.check', refId }
      });

      if (creditError) {
        const errorData = creditError as any;
        if (errorData.error === 'INSUFFICIENT_CREDITS') {
          toast.error(errorData.message || 'Insufficient credits', {
            action: { label: 'Get Credits', onClick: () => navigate('/pricing') },
          });
          return;
        }
        throw creditError;
      }

      // Phase 2: Call enhanced ATS analysis function
      const { data, error } = await supabase.functions.invoke("calculate-ats-score", {
        body: { 
          resume_id: resumeId,
          job_description: jobDescription || null,
          industry: selectedIndustry
        }
      });

      if (error) throw error;

      setAnalysis(data as ATSAnalysis);
      
      if (!creditResult.idempotent && creditResult.cost > 0) {
        toast.success(`ATS analysis complete! Used ${creditResult.cost} credits. ${creditResult.remaining} remaining.`);
      } else {
        toast.success("ATS analysis complete!");
      }

      // Refresh credits display
      window.dispatchEvent(new CustomEvent('creditsUpdated'));
    } catch (error: any) {
      console.error("[ATS] Analysis error:", error);
      toast.error(error.message || "Failed to analyze resume");
    } finally {
      setAnalyzing(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return "text-green-600";
    if (score >= 75) return "text-yellow-600";
    return "text-red-600";
  };

  const getScoreLabel = (score: number) => {
    if (score >= 90) return "Excellent";
    if (score >= 75) return "Good";
    if (score >= 60) return "Fair";
    return "Needs Work";
  };

  if (!hasFeature('atsScoreChecker')) {
    return (
        <SubscriptionGate feature="atsScoreChecker" requiredPlan="pro" />
    );
  }

  return (
    <>
      <Helmet>
        <title>ATS Score Checker - LoopBaan.ai</title>
        <meta name="description" content="Comprehensive ATS compatibility analysis for your resume. Get detailed scoring and actionable recommendations." />
      </Helmet>

      <DashboardLayout>
        <div className="p-6 space-y-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Target className="h-8 w-8 text-primary" />
              ATS Score Checker
            </h1>
            <p className="text-muted-foreground mt-2">
              Industry-leading ATS compatibility analysis with detailed recommendations
            </p>
          </div>

          {/* Upload/Select Section */}
          <Card className="p-6 mb-6">
            <div className="space-y-6">
              {/* Method Selection Tabs */}
              <div>
                <Label className="text-base font-semibold mb-3 block">
                  Choose Resume Source
                </Label>
                <Tabs value={uploadMethod} onValueChange={(v) => setUploadMethod(v as "select" | "upload")} className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="select">
                      <FileText className="mr-2 h-4 w-4" />
                      Select Saved Resume
                    </TabsTrigger>
                    <TabsTrigger value="upload">
                      <Upload className="mr-2 h-4 w-4" />
                      Upload New File
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="select" className="space-y-4 mt-4">
                    {/* Resume Versions */}
                    {resumes.length > 0 && (
                      <div>
                        <Label htmlFor="resume-select" className="text-sm font-medium mb-2 block">
                          Generated Resumes
                        </Label>
                        <Select value={selectedResumeId} onValueChange={(val) => {
                          setSelectedResumeId(val);
                          setSelectedDocumentId("");
                        }}>
                          <SelectTrigger id="resume-select">
                            <SelectValue placeholder="Choose a generated resume..." />
                          </SelectTrigger>
                          <SelectContent>
                            {resumes.map((resume) => (
                              <SelectItem key={resume.id} value={resume.id}>
                                Resume from {new Date(resume.created_at).toLocaleDateString()}
                                {resume.ats_score && ` (Score: ${resume.ats_score})`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Uploaded Documents */}
                    {documents.length > 0 && (
                      <div>
                        <Label htmlFor="document-select" className="text-sm font-medium mb-2 block">
                          My Documents
                        </Label>
                        <Select value={selectedDocumentId} onValueChange={(val) => {
                          setSelectedDocumentId(val);
                          setSelectedResumeId("");
                        }}>
                          <SelectTrigger id="document-select">
                            <SelectValue placeholder="Choose from your documents..." />
                          </SelectTrigger>
                          <SelectContent>
                            {documents.map((doc) => (
                              <SelectItem key={doc.id} value={doc.id}>
                                {doc.title} ({doc.format?.toUpperCase()})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {resumes.length === 0 && documents.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>No saved resumes found</p>
                        <p className="text-sm mt-1">Upload a file or generate a resume first</p>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="upload" className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="file-upload" className="text-sm font-medium mb-2 block">
                        Upload Resume File
                      </Label>
                      <div className="flex items-center gap-3">
                        <Input
                          id="file-upload"
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf,.docx,.doc,.txt"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          className="flex-1"
                        >
                          <Upload className="mr-2 h-4 w-4" />
                          {uploadedFile ? "Change File" : "Choose File"}
                        </Button>
                        {uploadedFile && (
                          <div className="flex items-center gap-2 flex-1 p-3 bg-muted rounded-md">
                            <FileText className="h-4 w-4 text-primary" />
                            <span className="text-sm truncate">{uploadedFile.name}</span>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        Accepted formats: PDF, DOCX, DOC, TXT (Max 5MB)
                      </p>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="industry" className="text-base font-semibold mb-2 block">
                    Industry (Optional)
                  </Label>
                  <Select value={selectedIndustry} onValueChange={setSelectedIndustry}>
                    <SelectTrigger id="industry">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tech">Tech/Software Engineering</SelectItem>
                      <SelectItem value="healthcare">Healthcare</SelectItem>
                      <SelectItem value="finance">Finance/Banking</SelectItem>
                      <SelectItem value="marketing">Marketing/Creative</SelectItem>
                      <SelectItem value="sales">Sales</SelectItem>
                      <SelectItem value="education">Education</SelectItem>
                      <SelectItem value="legal">Legal</SelectItem>
                      <SelectItem value="manufacturing">Manufacturing</SelectItem>
                      <SelectItem value="retail">Retail</SelectItem>
                      <SelectItem value="hospitality">Hospitality</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="job-level" className="text-base font-semibold mb-2 block">
                    Job Level (Optional)
                  </Label>
                  <Select value={selectedJobLevel} onValueChange={setSelectedJobLevel}>
                    <SelectTrigger id="job-level">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="entry">Entry Level</SelectItem>
                      <SelectItem value="mid">Mid Level</SelectItem>
                      <SelectItem value="senior">Senior Level</SelectItem>
                      <SelectItem value="executive">Executive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label htmlFor="job-description" className="text-base font-semibold">
                    Job Description (Optional - for keyword matching)
                  </Label>
                  {selectedJob && (
                    <Button variant="ghost" size="sm" onClick={clearSelectedJob}>
                      <X className="h-4 w-4 mr-1" />
                      Clear Job
                    </Button>
                  )}
                </div>
                
                {selectedJob && (
                  <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 mb-2">
                    <p className="text-sm">
                      Checking: <strong>{selectedJob.title}</strong> at <strong>{selectedJob.company}</strong>
                    </p>
                  </div>
                )}
                <Textarea
                  id="job-description"
                  placeholder="Paste the job description here to get keyword matching analysis..."
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                  rows={6}
                  className="resize-none"
                />
              </div>

              <Button 
                onClick={analyzeResume} 
                disabled={
                  (uploadMethod === "select" && !selectedResumeId && !selectedDocumentId) ||
                  (uploadMethod === "upload" && !uploadedFile) ||
                  analyzing
                }
                size="lg"
                className="w-full"
              >
                {analyzing ? (
                  <>
                    <RefreshCw className="mr-2 h-5 w-5 animate-spin" />
                    Analyzing Resume...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-5 w-5" />
                    Analyze ATS Compatibility
                  </>
                )}
              </Button>
            </div>
          </Card>

          {/* Analysis Results */}
          {analysis && (
            <div className="space-y-6">
              {/* Overall Score Card */}
              <Card className="p-8">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <ATSScoreGauge score={analysis.overall_score} />
                    <div className="text-center mt-4">
                      <div className={`text-3xl font-bold ${getScoreColor(analysis.overall_score)}`}>
                        {getScoreLabel(analysis.overall_score)}
                      </div>
                      <p className="text-muted-foreground mt-2">
                        Your resume scores in the{" "}
                        {analysis.overall_score >= 90 ? "excellent" : 
                         analysis.overall_score >= 75 ? "good" : 
                         analysis.overall_score >= 60 ? "fair" : "needs improvement"} range
                      </p>
                    </div>
                  </div>

                  {/* Before/After Comparison */}
                  {previousScore && (
                    <Card className="p-6 bg-muted/50">
                      <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <TrendingUp className="h-5 w-5" />
                        Score Comparison
                      </h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Previous Score:</span>
                          <span className="text-xl font-bold">{previousScore}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Current Score:</span>
                          <span className="text-xl font-bold text-primary">{analysis.overall_score}</span>
                        </div>
                        <Separator />
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold">Improvement:</span>
                          <span className={`text-xl font-bold ${
                            analysis.overall_score > previousScore ? "text-green-600" : "text-red-600"
                          }`}>
                            {analysis.overall_score > previousScore ? "+" : ""}
                            {analysis.overall_score - previousScore}
                          </span>
                        </div>
                        {analysis.estimated_improvement > 0 && (
                          <div className="mt-4 p-3 bg-primary/10 rounded-lg">
                            <p className="text-sm">
                              <strong>Potential Score:</strong> {analysis.overall_score + analysis.estimated_improvement}
                              <span className="text-muted-foreground ml-2">
                                (with recommended fixes)
                              </span>
                            </p>
                          </div>
                        )}
                      </div>
                    </Card>
                  )}
                </div>
              </Card>

              {/* Category Scores */}
              <ATSCategoryScores
                formatting={analysis.formatting_score}
                keywords={analysis.keywords_score}
                content={analysis.content_score}
                technical={analysis.technical_score}
              />

              {/* Detailed Analysis Tabs */}
              <Tabs defaultValue="comparison" className="w-full">
                <TabsList className="grid w-full grid-cols-7">
                  <TabsTrigger value="comparison">Comparison</TabsTrigger>
                  <TabsTrigger value="parser">Parser View</TabsTrigger>
                  <TabsTrigger value="issues">Issues</TabsTrigger>
                  <TabsTrigger value="keywords">Keywords</TabsTrigger>
                  <TabsTrigger value="compatibility">ATS Systems</TabsTrigger>
                  <TabsTrigger value="recommendations">Recommendations</TabsTrigger>
                  <TabsTrigger value="strengths">Strengths</TabsTrigger>
                </TabsList>

                <TabsContent value="comparison">
                  <ATSCompetitorComparison 
                    userScore={analysis.overall_score}
                    industry={selectedIndustry}
                    jobLevel={selectedJobLevel}
                  />
                </TabsContent>

                <TabsContent value="parser">
                  <ATSParsingPreview parsingData={analysis.parsing_preview} />
                </TabsContent>

                <TabsContent value="issues" className="space-y-4">
                  <ATSIssuesList
                    criticalIssues={analysis.critical_issues}
                    warnings={analysis.warnings}
                    opportunities={analysis.opportunities}
                  />
                </TabsContent>

                <TabsContent value="keywords">
                  {analysis.keyword_analysis ? (
                    <ATSKeywordAnalysis analysis={analysis.keyword_analysis} />
                  ) : (
                    <Card className="p-8 text-center">
                      <p className="text-muted-foreground">
                        Add a job description to get detailed keyword analysis
                      </p>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="compatibility">
                  <ATSSystemCompatibility systems={analysis.ats_compatibility} />
                </TabsContent>

                <TabsContent value="recommendations">
                  <ATSRecommendations
                    criticalIssues={analysis.critical_issues}
                    opportunities={analysis.opportunities}
                    industryInsights={analysis.industry_insights}
                  />
                </TabsContent>

                <TabsContent value="strengths">
                  <Card className="p-6">
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                      <CheckCircle2 className="h-6 w-6 text-green-600" />
                      What's Working Well
                    </h3>
                    <div className="grid gap-3">
                      {analysis.strengths.map((strength, index) => (
                        <div key={index} className="flex items-start gap-3 p-3 bg-muted/50 border border-green-600/30 rounded-lg">
                          <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-foreground">{strength}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                </TabsContent>
              </Tabs>

              {/* Action Buttons */}
              <Card className="p-6">
                <div className="flex flex-wrap gap-4 justify-center">
                  <Button variant="outline" size="lg">
                    <Download className="mr-2 h-5 w-5" />
                    Download Report (PDF)
                  </Button>
                  <Button variant="outline" size="lg" onClick={() => navigate("/resume")}>
                    <Eye className="mr-2 h-5 w-5" />
                    Edit Resume
                  </Button>
                  <Button size="lg" onClick={analyzeResume}>
                    <RefreshCw className="mr-2 h-5 w-5" />
                    Re-analyze
                  </Button>
                </div>
              </Card>
            </div>
          )}

          {/* Educational Content */}
          {!analysis && (
            <Card className="p-8 mt-6">
              <h2 className="text-2xl font-bold mb-4">What We Analyze</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary" />
                    Formatting & Structure (25 points)
                  </h3>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li>• File format compatibility</li>
                    <li>• Font usage and sizing</li>
                    <li>• Section header recognition</li>
                    <li>• Bullet point formatting</li>
                    <li>• Date consistency</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <Target className="h-5 w-5 text-primary" />
                    Keyword Optimization (30 points)
                  </h3>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li>• Job description matching</li>
                    <li>• Industry terminology</li>
                    <li>• Keyword density analysis</li>
                    <li>• Context relevance scoring</li>
                    <li>• Missing keyword identification</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <Zap className="h-5 w-5 text-primary" />
                    Content Parsing (25 points)
                  </h3>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li>• Contact info extraction</li>
                    <li>• Work experience clarity</li>
                    <li>• Education verification</li>
                    <li>• Quantifiable achievements</li>
                    <li>• Action verb usage</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                    Technical Checks (20 points)
                  </h3>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li>• No embedded images</li>
                    <li>• No text boxes or shapes</li>
                    <li>• Standard section headings</li>
                    <li>• Spell check compliance</li>
                    <li>• Acronym definitions</li>
                  </ul>
                </div>
              </div>
            </Card>
          )}
        </div>
      </DashboardLayout>
    </>
  );
};

export default AtsScoreChecker;
